import * as THREE from 'three'
import type RAPIER from '@dimforge/rapier3d-compat'
import type { DebugMaterialRegistry } from '../debug/debug-materials'
import { colliderRegistry } from '../world/collider-registry'

export interface WallSpec {
  id?: string
  name: string
  sourceTag?: string
  width: number
  height: number
  depth: number
  position: { x: number; y: number; z: number }
  material: THREE.Material
}

export interface PlaneSpec {
  id?: string
  name: string
  sourceTag?: string
  width: number
  length: number
  position: { x: number; y: number; z: number }
  material: THREE.Material
  isCeiling?: boolean
}

export interface TrimSpec {
  id?: string
  name: string
  width: number
  height: number
  depth: number
  position: { x: number; y: number; z: number }
  color: number
}

export interface TargetSpec {
  id?: string
  name: string
  sourceTag?: string
  radius: number
  height: number
  position: { x: number; y: number; z: number }
  material: THREE.Material
}

export class PhysicsSceneBuilder {
  private readonly scene: THREE.Scene
  private readonly world: RAPIER.World
  private readonly rapier: typeof RAPIER
  private readonly debugRegistry?: DebugMaterialRegistry

  readonly meshes: THREE.Mesh[] = []
  readonly colliders: RAPIER.Collider[] = []

  constructor(
    scene: THREE.Scene,
    world: RAPIER.World,
    rapier: typeof RAPIER,
    debugRegistry?: DebugMaterialRegistry,
  ) {
    this.scene = scene
    this.world = world
    this.rapier = rapier
    this.debugRegistry = debugRegistry
  }

  addWall(spec: WallSpec): THREE.Mesh {
    const { width, height, depth, position, material, name, sourceTag = 'ESOTERIA/SECTOR : Wall' } = spec
    const id = spec.id ?? name.toLowerCase().replace(/[^a-z0-9]/g, '_')

    // 1. Visual Mesh
    const geo = new THREE.BoxGeometry(width, height, depth)
    const mesh = new THREE.Mesh(geo, material)
    mesh.position.set(position.x, position.y, position.z)
    this.scene.add(mesh)
    this.meshes.push(mesh)

    // 2. Physics Body & Collider
    const bodyDesc = this.rapier.RigidBodyDesc.fixed().setTranslation(position.x, position.y, position.z)
    const body = this.world.createRigidBody(bodyDesc)
    const colDesc = this.rapier.ColliderDesc.cuboid(width * 0.5, height * 0.5, depth * 0.5)
    const col = this.world.createCollider(colDesc, body)
    this.colliders.push(col)

    // 3. Registries (Collider + Debug)
    colliderRegistry.register(col.handle, { id, name, sourceTag, category: 'wall' })
    this.debugRegistry?.registerMesh(mesh, 'wall', material, {
      id,
      name,
      sourceTag,
      dimensions: [width, height, depth],
    })

    return mesh
  }

  addFloor(spec: PlaneSpec): THREE.Mesh {
    const { width, length, position, material, name, sourceTag = 'ESOTERIA/SECTOR : Floor' } = spec
    const id = spec.id ?? 'floor_' + name.toLowerCase().replace(/[^a-z0-9]/g, '_')

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(width, length), material)
    floor.rotation.x = -Math.PI / 2
    floor.position.set(position.x, position.y, position.z)
    this.scene.add(floor)
    this.meshes.push(floor)

    const floorBody = this.world.createRigidBody(this.rapier.RigidBodyDesc.fixed())
    const floorCol = this.world.createCollider(
      this.rapier.ColliderDesc.cuboid(width * 0.5, 0.5, length * 0.5).setTranslation(
        position.x,
        position.y - 0.5,
        position.z,
      ),
      floorBody,
    )
    this.colliders.push(floorCol)

    colliderRegistry.register(floorCol.handle, { id, name, sourceTag, category: 'floor' })
    this.debugRegistry?.registerMesh(floor, 'floor', material, {
      id,
      name,
      sourceTag,
      dimensions: [width, 0.1, length],
    })

    return floor
  }

  addCeiling(spec: PlaneSpec): THREE.Mesh {
    const { width, length, position, material, name, sourceTag = 'ESOTERIA/SECTOR : Ceiling' } = spec
    const id = spec.id ?? 'ceiling_' + name.toLowerCase().replace(/[^a-z0-9]/g, '_')

    const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(width, length), material)
    ceiling.rotation.x = Math.PI / 2
    ceiling.position.set(position.x, position.y, position.z)
    this.scene.add(ceiling)
    this.meshes.push(ceiling)

    const ceilingBody = this.world.createRigidBody(this.rapier.RigidBodyDesc.fixed())
    const ceilingCol = this.world.createCollider(
      this.rapier.ColliderDesc.cuboid(width * 0.5, 0.5, length * 0.5).setTranslation(
        position.x,
        position.y + 0.5,
        position.z,
      ),
      ceilingBody,
    )
    this.colliders.push(ceilingCol)

    colliderRegistry.register(ceilingCol.handle, { id, name, sourceTag, category: 'ceiling' })
    this.debugRegistry?.registerMesh(ceiling, 'ceiling', material, {
      id,
      name,
      sourceTag,
      dimensions: [width, 0.1, length],
    })

    return ceiling
  }

  addTrim(spec: TrimSpec): THREE.Mesh {
    const { width, height, depth, position, color, name } = spec
    const id = spec.id ?? name.toLowerCase().replace(/[^a-z0-9]/g, '_')

    const geo = new THREE.BoxGeometry(width, height, depth)
    const mat = new THREE.MeshBasicMaterial({ color })
    const mesh = new THREE.Mesh(geo, mat)
    mesh.position.set(position.x, position.y, position.z)
    this.scene.add(mesh)
    this.meshes.push(mesh)

    this.debugRegistry?.registerMesh(mesh, 'trim', mat, {
      id,
      name,
      sourceTag: 'Diegetic HUD Architectural Trim',
      dimensions: [width, height, depth],
    })

    return mesh
  }

  addTarget(spec: TargetSpec): THREE.Mesh {
    const { radius, height, position, material, name, sourceTag = 'ESOTERIA/SECTOR : Target' } = spec
    const id = spec.id ?? name.toLowerCase().replace(/[^a-z0-9]/g, '_')

    const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, height, 16), material)
    mesh.position.set(position.x, position.y, position.z)
    this.scene.add(mesh)
    this.meshes.push(mesh)

    const body = this.world.createRigidBody(
      this.rapier.RigidBodyDesc.fixed().setTranslation(position.x, position.y, position.z),
    )
    const col = this.world.createCollider(this.rapier.ColliderDesc.cylinder(height * 0.5, radius), body)
    this.colliders.push(col)

    colliderRegistry.register(col.handle, { id, name, sourceTag, category: 'target' })
    this.debugRegistry?.registerMesh(mesh, 'target', material, {
      id,
      name,
      sourceTag,
      dimensions: [radius * 2, height, radius * 2],
    })

    return mesh
  }
}
